import { useEffect } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Primitive, Skeleton, VStack } from '@rtkwlf/fenrir-react';
import { faExclamationTriangle } from '@rtkwlf/pro-solid-svg-icons/faExclamationTriangle';
import { faRotateBack } from '@rtkwlf/pro-solid-svg-icons/faRotateBack';
import { useQuery } from '@tanstack/react-query';

import { useGetOrganizationSecurityFocusOptions } from '@apis/track-public/query-options';
import { Stack } from '@packages/fenrir/components';
import { useDocumentTitle } from '@packages/fenrir/hooks/use-document-title';
import { PagePlain } from '@packages/fenrir/pages/page-plain';
import { GeneralWrapper } from 'ae-components/generalWrapper/GeneralWrapper';

import { WidgetError } from '../../../components/widget/widget-error';
import { routes } from '../../../constants/routes';
import { useFocusIDParam } from '../../../hooks/use-focus-id-param';
import { useSecurityJourneyTranslations } from '../../../locales/use-security-journey-translations';
import { Breadcrumbs } from '../../_layout/components/breadcrumbs';
import { usePageMeta } from '../../_layout/components/page-meta-provider';

import { SecurityFocusDetails } from './components/security-focus-details';
import { SecurityFocusHeader } from './components/security-focus-header';
import { SecurityFocusSpidrsTable } from './components/security-focus-spidrs-table';
import { SecurityFocusTimeline } from './components/security-focus-timeline';
import { SecurityFocusUpcomingSpidrsTable } from './components/security-focus-upcoming-spidrs-table';

export const FocusIDPage = () => {
  /**
   * Subscribe to page meta
   */
  const { setCrumbs } = usePageMeta();

  /**
   * Init translations
   */
  const { t } = useSecurityJourneyTranslations();

  /**
   * Get focusID param from url
   */
  const { focusID } = useFocusIDParam();

  const {
    data: focus,
    isLoading,
    isError,
    refetch,
  } = useQuery(
    useGetOrganizationSecurityFocusOptions({
      focusID,
    }),
  );

  /**
   * Set the document title
   */
  useDocumentTitle(t('focusID.documentTitle', { name: focus?.name }));

  /**
   * Update page meta
   */
  useEffect(() => {
    setCrumbs([
      {
        type: 'intl',
        title: t('common.navigation.allFocuses'),
        href: routes.securityFocuses.index(),
      },
      {
        type: 'dynamic',
        title: focus?.name ?? focusID,
        href: '/',
        disabled: true,
      },
    ]);

    return () => {
      setCrumbs([]);
    };
  }, [focus, setCrumbs, t, focusID]);

  return (
    <Stack>
      <Breadcrumbs />
      <Primitive.div
        padding="medium"
        boxShadow="default"
        alignSelf="stretch"
        borderRadius="default"
        backgroundColor="widgetDefault"
      >
        <GeneralWrapper
          isLoading={isLoading}
          isError={isError}
          loader={
            <Skeleton
              data-testid="security-focus-loading"
              width="full"
              height="10rem"
              margin="zero"
            />
          }
          error={
            <WidgetError
              data-testid="security-focus-error"
              actionIcon={<FontAwesomeIcon icon={faRotateBack} size="sm" />}
              actionText={t('common.retry')}
              heading={t('focusID.error.title')}
              description={t('common.clickToRetry')}
              icon={<FontAwesomeIcon icon={faExclamationTriangle} size="2x" />}
              onActionClick={() => refetch()}
            />
          }
        >
          {focus && (
            <VStack height="full" yAlign="top" gap="xlarge">
              <SecurityFocusHeader
                focusName={focus.name}
                focusState={focus.state}
                startedAt={focus.startedAt}
                completedAt={focus.completedAt}
              />
              <SecurityFocusDetails
                focusGoals={focus.goals}
                focusOutcomes={focus.outcomes}
                isDetour={focus.detour}
              />
              <SecurityFocusTimeline focus={focus} />
              <SecurityFocusSpidrsTable focus={focus} />
              <SecurityFocusUpcomingSpidrsTable focus={focus} />
            </VStack>
          )}
        </GeneralWrapper>
      </Primitive.div>
    </Stack>
  );
};

export const Component = () => (
  // document title is set in FocusIDPage
  <PagePlain>
    <FocusIDPage />
  </PagePlain>
);
